import { createContext, useContext, type ReactNode } from 'react';
import type { ArtifactBody, ArtifactEnvelope } from '@pf/contract';

export interface ArtifactContextValue {
  body: ArtifactBody;
  envelope: ArtifactEnvelope;
  /**
   * The 32-byte AES-256-GCM session key. Held in memory only; cleared on lock.
   * Used by the passkey enrolment flow (Settings) to wrap the key for future
   * device-based unlocks.
   */
  sessionKey: Uint8Array;
  lock: () => void;
  /** Switches the active user: re-locks, updates localStorage, triggers re-fetch. */
  switchUser: (userId: string) => void;
  activeUserId: string;
}

const ArtifactContext = createContext<ArtifactContextValue | null>(null);

export function ArtifactProvider({
  body,
  envelope,
  sessionKey,
  lock,
  switchUser,
  activeUserId,
  children,
}: ArtifactContextValue & { children: ReactNode }) {
  return (
    <ArtifactContext.Provider value={{ body, envelope, sessionKey, lock, switchUser, activeUserId }}>
      {children}
    </ArtifactContext.Provider>
  );
}

export function useArtifact(): ArtifactContextValue {
  const ctx = useContext(ArtifactContext);
  if (!ctx) {
    throw new Error('useArtifact must be used inside ArtifactProvider');
  }
  return ctx;
}
